/**
 * AuthorWorkbench — eDMS Web
 *
 * Primary screen for ~80% of users (per UX spec §11.2 / wireframe #4-existing).
 *
 * Data sources (all via TanStack Query against mock-api on port 8081;
 * vite proxies /api to it during dev). Each panel renders its own
 * loading/error state so a single slow query doesn't blank the whole screen.
 *
 * Source: drafts/files/04_author_workbench.html (existing wireframe)
 *       + drafts/31_ux_design_system_spec.md §5 Flow 1
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Button, StatusBadge, PageHero, StatCard, Stagger, Skeleton, type DocumentState } from '@edms/design-system';
import styles from './AuthorWorkbench.module.css';
import {
  useActiveWorkflows,
  useCurrentUser,
  useDocuments,
  useMyTasks,
  useTrainingStats,
  type ActiveWorkflow,
  type DocumentSummary,
  type MyTask,
} from '../../api/hooks';
import { NewDocumentForm } from './NewDocumentForm';
import { BulkUploadForm } from './BulkUploadForm';
import { StartWorkflowModal } from './StartWorkflowModal';

export function AuthorWorkbench() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userQ = useCurrentUser();
  const docsQ = useDocuments();
  const workflowsQ = useActiveWorkflows();
  const tasksQ = useMyTasks();
  const trainingQ = useTrainingStats();

  // Dialog state — Phase 1 closure.
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [startWfFor, setStartWfFor] = useState<DocumentSummary | null>(null);

  const drafts = (docsQ.data?.items ?? []).filter((d) => d.status === 'DRAFT' || d.status === 'REVIEW_REJECTED');
  const draftCount = drafts.length;
  const taskCount = tasksQ.data?.items.length ?? 0;
  const trainingPct = trainingQ.data?.completion_pct ?? 0;
  const overdueCount = trainingQ.data?.overdue_count ?? 0;

  return (
    <div className={styles.workbench}>
      {/* Welcome banner — gradient PageHero */}
      <PageHero
        eyebrow={t('workbench.eyebrow', { defaultValue: 'Documents' })}
        title={t('workbench.title')}
        subtitle={t('workbench.subtitle', { name: userQ.data?.full_name ?? '…' })}
        actions={
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Button
              variant="primary"
              size="lg"
              leadingIcon="+"
              onClick={() => setNewDocOpen(true)}
            >
              {t('workbench.actions.newDocument')}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              leadingIcon="⇪"
              onClick={() => setBulkOpen(true)}
            >
              {t('workbench.actions.bulkUpload', { defaultValue: 'Bulk upload' })}
            </Button>
          </div>
        }
      />

      {/* New document modal */}
      <NewDocumentForm
        isOpen={newDocOpen}
        onClose={() => setNewDocOpen(false)}
        onCreated={(response) => {
          // Refresh drafts list so the new doc appears immediately.
          docsQ.refetch();
          // Jump straight into the editor for the new draft — the user
          // just typed a title and (optionally) attached a file, so
          // showing them their new doc open in the editor is the
          // expected outcome. From there the toolbar's "Submit for
          // Review" CTA on the drafts table is one click away.
          const newId = response.document_id ?? response.id;
          if (newId) {
            navigate({ to: '/documents/$id/edit', params: { id: newId } });
          }
        }}
      />

      {/* Bulk / folder upload modal (E-4) — one draft per file, each stored to
          MinIO (E-2) and auto-OCR'd (E-3). Stays on this screen; just refreshes. */}
      <BulkUploadForm
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => { docsQ.refetch(); }}
      />

      {/* Start workflow modal */}
      {startWfFor && (
        <StartWorkflowModal
          isOpen={true}
          documentVersionId={startWfFor.id}
          departmentId={startWfFor.department_id ?? undefined}
          documentLabel={`${startWfFor.doc_number}${startWfFor.title ? ' — ' + startWfFor.title : ''}`}
          onClose={() => setStartWfFor(null)}
          onStarted={(response) => {
            setStartWfFor(null);
            // Navigate to the reviewer/workflow detail screen for the new workflow.
            navigate({ to: '/workflows/$id', params: { id: response.workflow_id } });
          }}
        />
      )}

      {/* KPI strip — StatCards (Stagger reveal + count-up; Skeleton while loading) */}
      <Stagger className={styles.kpiStrip} step={50} aria-label="Key indicators">
        {docsQ.isLoading ? (
          <Skeleton height={92} radius={14} />
        ) : (
          <StatCard
            label={t('workbench.kpis.myDrafts')}
            value={draftCount}
            accent="var(--spectro-purple, #9333EA)"
          />
        )}
        {tasksQ.isLoading ? (
          <Skeleton height={92} radius={14} />
        ) : (
          <StatCard
            label={t('workbench.kpis.myTasks')}
            value={taskCount}
            accent={taskCount > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--spectro-purple, #9333EA)'}
          />
        )}
        {trainingQ.isLoading ? (
          <Skeleton height={92} radius={14} />
        ) : (
          <StatCard
            label={t('workbench.kpis.trainingCurrency')}
            value={trainingPct}
            suffix="%"
            accent={trainingPct >= 95 ? 'var(--color-status-success, #16A34A)' : 'var(--color-status-warning, #D97706)'}
          />
        )}
        <StatCard label={t('workbench.kpis.documentsIOwn')} value={14} accent="var(--spectro-blue, #0065FF)" />
      </Stagger>

      {/* Main content: left section (drafts + workflows) + right rail (tasks + training) */}
      <div className={styles.mainGrid}>
        <div className={styles.leftCol}>
          {/* Drafts table */}
          <section className={styles.panel} aria-labelledby="drafts-heading">
            <header className={styles.panelHeader}>
              <h2 id="drafts-heading" className={styles.panelTitle}>
                {t('workbench.sections.drafts')}
              </h2>
              <Button variant="tertiary" size="sm">
                {t('workbench.actions.viewAll')}
              </Button>
            </header>
            <DraftsTable
              isLoading={docsQ.isLoading}
              isError={docsQ.isError}
              drafts={drafts}
              emptyLabel={t('workbench.empty.drafts')}
              editLabel={t('common.edit')}
              submitLabel={t('workbench.actions.submitForReview')}
              onEdit={(draft) => navigate({ to: '/documents/$id/edit', params: { id: draft.id } })}
              onSubmitForReview={(draft) => setStartWfFor(draft)}
              cols={{
                document: t('workbench.columns.document'),
                type: t('workbench.columns.type'),
                status: t('workbench.columns.status'),
                lastSaved: t('workbench.columns.lastSaved'),
                action: t('workbench.columns.action'),
              }}
            />
          </section>

          {/* Active workflows */}
          <section className={styles.panel} aria-labelledby="workflows-heading">
            <header className={styles.panelHeader}>
              <h2 id="workflows-heading" className={styles.panelTitle}>
                {t('workbench.sections.activeWorkflows')}
              </h2>
            </header>
            <WorkflowsTable
              isLoading={workflowsQ.isLoading}
              isError={workflowsQ.isError}
              workflows={workflowsQ.data?.items ?? []}
              cols={{
                document: t('workbench.columns.document'),
                stage: 'Stage',
                sla: t('workbench.columns.sla'),
              }}
            />
          </section>
        </div>

        <div className={styles.rightCol}>
          {/* My Tasks */}
          <section className={styles.panel} aria-labelledby="tasks-heading">
            <header className={styles.panelHeader}>
              <h2 id="tasks-heading" className={styles.panelTitle}>
                {t('workbench.sections.myTasks')}
              </h2>
            </header>
            <TasksPanel
              isLoading={tasksQ.isLoading}
              tasks={tasksQ.data?.items ?? []}
              emptyLabel={t('workbench.empty.tasks')}
            />
          </section>

          {/* Training Status */}
          <section className={styles.panel} aria-labelledby="training-heading">
            <header className={styles.panelHeader}>
              <h2 id="training-heading" className={styles.panelTitle}>
                {t('workbench.sections.training')}
              </h2>
            </header>
            <div className={styles.trainingSummary}>
              <div className={styles.trainingPercent}>
                <strong>{trainingQ.isLoading ? '…' : `${trainingPct}%`}</strong>
                <span>complete</span>
              </div>
              {overdueCount > 0 && (
                <div className={styles.trainingOverdue} role="alert">
                  <strong>{overdueCount}</strong> overdue
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

interface DraftsTableProps {
  isLoading: boolean;
  isError: boolean;
  drafts: DocumentSummary[];
  emptyLabel: string;
  editLabel: string;
  submitLabel: string;
  onEdit: (draft: DocumentSummary) => void;
  onSubmitForReview: (draft: DocumentSummary) => void;
  cols: Record<'document' | 'type' | 'status' | 'lastSaved' | 'action', string>;
}

function DraftsTable({
  isLoading,
  isError,
  drafts,
  emptyLabel,
  editLabel,
  submitLabel,
  onEdit,
  onSubmitForReview,
  cols,
}: DraftsTableProps) {
  if (isLoading) return <p className={styles.empty} role="status">Loading drafts…</p>;
  if (isError) return <p className={styles.empty} role="alert">Failed to load drafts.</p>;
  if (drafts.length === 0) return <p className={styles.empty}>{emptyLabel}</p>;

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">{cols.document}</th>
          <th scope="col">{cols.type}</th>
          <th scope="col">{cols.status}</th>
          <th scope="col">{cols.lastSaved}</th>
          <th scope="col">{cols.action}</th>
        </tr>
      </thead>
      <tbody>
        {drafts.map((d) => (
          <tr key={d.id}>
            <td>
              <div className={styles.docCell}>
                <strong>{d.doc_number}</strong>
                <span>{d.title}</span>
              </div>
            </td>
            <td>{d.doc_type_code}</td>
            <td>
              <StatusBadge state={d.status as DocumentState} />
            </td>
            <td className={styles.numeric}>{d.last_saved}</td>
            <td>
              <div className={styles.rowActions}>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => onEdit(d)}
                >
                  {editLabel}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onSubmitForReview(d)}
                >
                  {submitLabel}
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface WorkflowsTableProps {
  isLoading: boolean;
  isError: boolean;
  workflows: ActiveWorkflow[];
  cols: Record<'document' | 'stage' | 'sla', string>;
}

function WorkflowsTable({ isLoading, isError, workflows, cols }: WorkflowsTableProps) {
  if (isLoading) return <p className={styles.empty} role="status">Loading workflows…</p>;
  if (isError) return <p className={styles.empty} role="alert">Failed to load workflows.</p>;
  if (workflows.length === 0) return <p className={styles.empty}>No active workflows.</p>;

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">{cols.document}</th>
          <th scope="col">{cols.stage}</th>
          <th scope="col">{cols.sla}</th>
        </tr>
      </thead>
      <tbody>
        {workflows.map((w) => (
          <tr key={w.id}>
            <td>
              <strong>{w.doc_number}</strong>
            </td>
            <td>{w.stage}</td>
            <td>
              <span className={`${styles.slaPill} ${styles[`sla-${w.sla_state}`]}`}>{w.sla}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface TasksPanelProps {
  isLoading: boolean;
  tasks: MyTask[];
  emptyLabel: string;
}

function TasksPanel({ isLoading, tasks, emptyLabel }: TasksPanelProps) {
  if (isLoading) return <p className={styles.empty} role="status">Loading tasks…</p>;
  if (tasks.length === 0) return <p className={styles.empty}>{emptyLabel}</p>;
  return (
    <ul className={styles.taskList}>
      {tasks.map((task) => (
        <li key={task.id} className={`${styles.taskItem} ${task.urgent ? styles.urgent : ''}`}>
          <span className={styles.taskLabel}>{task.label}</span>
          <span className={styles.taskDue}>{task.due}</span>
        </li>
      ))}
    </ul>
  );
}
